import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { PedidosNuevosAPI } from '../../services/pedidosNuevosService';
import type { BorradorPendiente } from '../../types/pedidosNuevos';
import { formatearFechaMedia } from '../../utils/dateUtils';

interface BannerBorradoresPendientesProps {
  /**
   * Callback al click en un borrador. Recibe clienteId y minutaId
   * para que el caller setee los dropdowns de Armar Pedido y dispare
   * la carga de la grilla con las cantidades guardadas.
   */
  onSeleccionar: (clienteId: number, minutaId: number) => void;
  /**
   * Cuando este valor cambia, el componente recarga el endpoint.
   * Útil tras confirmar un pedido — el caller incrementa un
   * counter y el banner se refresca.
   */
  refreshKey?: number;
  /**
   * Cantidad máxima de items mostrados en estado colapsado.
   * Default 5. Si hay más, aparece "Ver N más" / "Ver menos".
   */
  maxVisible?: number;
}

const SKELETON_TIMEOUT_MS = 1500;

/**
 * Banner de "Pedidos por confirmar" — muestra TODOS los borradores
 * en minutas vigentes para CUALQUIER usuario autenticado.
 *
 * Decisión operativa (Jorge 2026-05-12): sin filtros por rol ni por
 * usuario. En QuickTaste los borradores los crea casi siempre el
 * admin transcribiendo para clientes — filtrar por
 * creado_por_usuario_id dejaba a los clientes sin banner. La idea
 * es que todo el equipo vea los borradores abiertos para cerrarlos
 * antes de la operación del día.
 *
 * Comportamiento:
 * - Carga lazy al montar y cuando cambia refreshKey.
 * - Skeleton inicial limitado a 1.5s (luego se oculta para no
 *   "atascar" la pantalla si el endpoint tarda).
 * - Falla silenciosa: si el endpoint devuelve error o array vacío,
 *   el banner no se muestra (no es funcionalidad crítica).
 * - "Ver más" expande la lista; click fuera o "Ver menos" la
 *   colapsa.
 * - Respeta el modo dark/light de la app via clases dark:.
 *
 * Reusable: no asume que está en ArmarPedido. Cualquier pantalla
 * puede montarlo pasando su propio onSeleccionar.
 */
const BannerBorradoresPendientes: React.FC<BannerBorradoresPendientesProps> = ({
  onSeleccionar,
  refreshKey = 0,
  maxVisible = 5,
}) => {
  const [items, setItems] = useState<BorradorPendiente[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [skeletonVisible, setSkeletonVisible] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Carga del endpoint para cualquier usuario autenticado. Falla
  // silenciosa: si hay error, items queda vacío y el banner se
  // oculta.
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await PedidosNuevosAPI.obtenerBorradoresPendientes();
      setItems(resp.data || []);
    } catch (err) {
      console.warn(
        '[BannerBorradoresPendientes] error consultando endpoint, banner oculto:',
        err
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar, refreshKey]);

  // Timeout del skeleton — si tarda más de 1.5s lo escondemos para
  // no entorpecer la pantalla. La lista igual aparece cuando llegue.
  useEffect(() => {
    if (!loading) {
      setSkeletonVisible(false);
      return;
    }
    setSkeletonVisible(true);
    const t = window.setTimeout(
      () => setSkeletonVisible(false),
      SKELETON_TIMEOUT_MS
    );
    return () => window.clearTimeout(t);
  }, [loading]);

  // Click afuera → colapsar si estaba expandido.
  useEffect(() => {
    if (!expanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded]);

  // No mostrar si está cargando con skeleton ya oculto, o si no
  // hay items (todos los roles cualifican).
  if (loading && skeletonVisible) {
    return (
      <div className="mb-4 rounded-lg border border-yellow-200 dark:border-yellow-800/60 bg-yellow-50 dark:bg-yellow-900/15 px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-4 w-4 rounded bg-yellow-200/70 dark:bg-yellow-700/40 animate-pulse" />
          <div className="h-3.5 w-56 rounded bg-yellow-200/70 dark:bg-yellow-700/40 animate-pulse" />
        </div>
        <div className="h-3 w-72 rounded bg-yellow-100 dark:bg-yellow-800/30 animate-pulse" />
      </div>
    );
  }
  if (loading && !skeletonVisible) return null;
  if (items.length === 0) return null;

  const visibles = expanded ? items : items.slice(0, maxVisible);
  const restantes = Math.max(items.length - maxVisible, 0);
  const total = items.length;
  // Copy unificado para todos los roles: el banner muestra todos
  // los borradores del sistema y la columna "por X" siempre aparece.
  const headline = `Hay ${total} ${
    total === 1 ? 'pedido guardado' : 'pedidos guardados'
  } sin confirmar en el sistema`;
  const subtitulo =
    'Borradores en minutas vigentes — completalos para enviar.';

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label="Pedidos por confirmar"
      aria-live="polite"
      className="mb-4 rounded-lg border border-yellow-300/80 dark:border-yellow-700/70 bg-yellow-50 dark:bg-yellow-900/20 overflow-hidden shadow-sm"
      style={{ animation: 'fadeUp 0.4s ease-out both' }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-yellow-200/80 dark:border-yellow-800/60">
        <div className="flex items-start gap-3">
          <AlertTriangle
            className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 leading-tight">
              {headline}
            </h3>
            <p className="text-xs text-yellow-800/80 dark:text-yellow-200/70 mt-0.5">
              {subtitulo}
            </p>
          </div>
        </div>
      </div>

      {/* Lista */}
      <ul className="divide-y divide-yellow-200/70 dark:divide-yellow-800/40">
        {visibles.map((b, idx) => {
          const clienteLabel =
            b.cliente_razon_social || b.cliente_nombre || '—';
          const codigoCliente = b.cliente_codigo
            ? `${b.cliente_codigo} · `
            : '';
          const recetasLabel =
            b.total_recetas === 1
              ? '1 receta'
              : `${b.total_recetas} recetas`;
          const creadoPor = b.creado_por_nombre || b.creado_por_email || '';

          return (
            <li
              key={b.id}
              style={{
                animation: `fadeUp 0.35s ${Math.min(idx * 0.03, 0.2)}s ease-out both`,
              }}
            >
              <button
                type="button"
                onClick={() => onSeleccionar(b.cliente_id, b.minuta_id)}
                className="w-full text-left px-4 py-2.5 hover:bg-yellow-100/70 dark:hover:bg-yellow-900/40 focus:outline-none focus-visible:bg-yellow-100 dark:focus-visible:bg-yellow-900/50 transition-colors group"
                aria-label={`Continuar borrador ${b.codigo} de ${clienteLabel}, entrega ${formatearFechaMedia(b.fecha_entrega)}, ${recetasLabel}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-yellow-900 dark:text-yellow-100 font-medium flex-shrink-0">
                    {b.codigo}
                  </span>
                  <span className="text-sm text-yellow-900 dark:text-yellow-100 truncate flex-1 min-w-0">
                    <span className="text-yellow-800 dark:text-yellow-200">
                      {codigoCliente}
                      {clienteLabel}
                    </span>
                  </span>
                  <span className="text-xs text-yellow-800/80 dark:text-yellow-200/80 flex-shrink-0">
                    {formatearFechaMedia(b.fecha_entrega)} · {recetasLabel}
                    {creadoPor && (
                      <>
                        {' · '}
                        <span className="text-yellow-700/80 dark:text-yellow-300/70">
                          por {creadoPor}
                        </span>
                      </>
                    )}
                  </span>
                  <span className="hidden sm:inline-flex items-center text-xs font-medium text-yellow-900 dark:text-yellow-100 opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    Continuar
                    <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {/* "Ver N más" / "Ver menos" */}
      {restantes > 0 && (
        <div className="px-4 py-2 border-t border-yellow-200/70 dark:border-yellow-800/50 bg-yellow-50/60 dark:bg-yellow-900/10">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100 focus:outline-none focus-visible:underline transition-colors"
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                Ver menos
                <ChevronUp className="w-3.5 h-3.5" />
              </>
            ) : (
              <>
                Ver {restantes} más
                <ChevronDown className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </div>
      )}

      {/* Keyframe local — sin librería de animación */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default BannerBorradoresPendientes;
